import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, hierarchyApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import toast from 'react-hot-toast';
import { Users, Plus, Search, Filter, Mail, Edit, ShieldAlert, KeyRound, UploadCloud, ChevronRight, ChevronDown } from 'lucide-react';
import { useThemeStore } from '@/store/themeStore';

export default function UserManagementPage() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingUser, setEditingUser] = useState<any>(null);
  const [onProbation, setOnProbation] = useState(false);

  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const { data: rawUsers = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await userApi.getAll();
      return Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => userApi.create(data),
    onSuccess: () => {
      toast.success('User created successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to create user');
    }
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => userApi.deactivate(id),
    onSuccess: () => {
      toast.success('User deactivated');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: any }) => userApi.update(id, data),
    onSuccess: () => {
      toast.success('User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update user');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => userApi.adminResetPassword(id),
    onSuccess: () => { toast.success('Password reset to NeuralNet@2026'); },
    onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to reset password'); }
  });

  const openCreateModal = () => { setEditingUser(null); setOnProbation(false); setIsModalOpen(true); };
  const openEditModal = (user: any) => { setEditingUser(user); setOnProbation(!!user?.probationEndDate); setIsModalOpen(true); };

  useEffect(() => {
    if (rawUsers && Array.isArray(rawUsers) && rawUsers.length > 0) {
      const toExpand = rawUsers.filter((u: any) => u.role === 'MD').map((u: any) => u.id);
      setExpandedRows(new Set(toExpand));
    }
  }, [rawUsers.length]);

  const isFiltering = searchTerm !== '' || roleFilter !== '';

  const validUsers = useMemo(() => {
    const users = Array.isArray(rawUsers) ? rawUsers : [];
    return users.filter((u: any) => u.employeeId !== 'ADMIN-001');
  }, [rawUsers]);

  const usersTree = useMemo(() => {
    const userMap = new Map(validUsers.map((u: any) => [u.id, { ...u, children: [] }]));
    const roots: any[] = [];
    userMap.forEach(user => {
      if (user.managerId && userMap.has(user.managerId)) {
        userMap.get(user.managerId).children.push(user);
      } else {
        roots.push(user);
      }
    });
    return roots;
  }, [validUsers]);

  const filteredFlatUsers = useMemo(() => {
    return validUsers.filter((u: any) => {
      const matchesSearch = (u.name + u.email + u.employeeId).toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter ? u.role === roleFilter : true;
      return matchesSearch && matchesRole;
    });
  }, [validUsers, searchTerm, roleFilter]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      employeeId: formData.get('employeeId'),
      name: formData.get('name'),
      email: formData.get('email'),
      role: formData.get('role'),
      department: formData.get('department'),
      designation: formData.get('designation'),
      dateOfJoining: formData.get('dateOfJoining'),
      probationEndDate: formData.get('probationEndDate') || null,
      gender: formData.get('gender') || null,
      hierarchyLevelId: formData.get('hierarchyLevelId') || null,
      managerId: formData.get('managerId') || null,
    };
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const toggleExpand = (id: string) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setExpandedRows(newSet);
  };

  // Theme-aware badge helper
  const getRoleBadge = (role: string) => {
    if (role === 'MD') return (
      <span style={{
        background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2',
        color: isDark ? '#f87171' : '#b91c1c',
        border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}`,
      }} className="px-2 py-0.5 rounded text-xs font-bold">Super User</span>
    );
    if (role === 'ORG_ADMIN') return (
      <span style={{
        background: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7',
        color: isDark ? '#fbbf24' : '#92400e',
        border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : '#fde68a'}`,
      }} className="px-2 py-0.5 rounded text-xs font-bold">Org Admin</span>
    );
    return (
      <span style={{
        background: isDark ? 'rgba(100,116,139,0.15)' : '#f1f5f9',
        color: isDark ? '#94a3b8' : '#475569',
        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      }} className="px-2 py-0.5 rounded text-xs font-bold">User</span>
    );
  };

  const getGenderBadge = (gender: string) => {
    if (gender === 'MALE') return (
      <span style={{
        background: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe',
        color: isDark ? '#60a5fa' : '#1d4ed8',
        border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}`,
      }} className="px-2 py-1 rounded text-xs font-semibold">Male</span>
    );
    if (gender === 'FEMALE') return (
      <span style={{
        background: isDark ? 'rgba(236,72,153,0.15)' : '#fce7f3',
        color: isDark ? '#f472b6' : '#9d174d',
        border: `1px solid ${isDark ? 'rgba(236,72,153,0.3)' : '#fbcfe8'}`,
      }} className="px-2 py-1 rounded text-xs font-semibold">Female</span>
    );
    if (gender === 'OTHER') return (
      <span style={{
        background: isDark ? 'rgba(139,92,246,0.15)' : '#ede9fe',
        color: isDark ? '#a78bfa' : '#5b21b6',
        border: `1px solid ${isDark ? 'rgba(139,92,246,0.3)' : '#ddd6fe'}`,
      }} className="px-2 py-1 rounded text-xs font-semibold">Other</span>
    );
    return <span style={{ color: isDark ? '#475569' : '#94a3b8' }}>—</span>;
  };

  const getStatusBadge = (isActive: boolean) => (
    <span style={{
      background: isActive
        ? (isDark ? 'rgba(22,163,74,0.15)' : '#dcfce7')
        : (isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2'),
      color: isActive
        ? (isDark ? '#4ade80' : '#15803d')
        : (isDark ? '#f87171' : '#b91c1c'),
      border: `1px solid ${isActive
        ? (isDark ? 'rgba(22,163,74,0.3)' : '#bbf7d0')
        : (isDark ? 'rgba(239,68,68,0.3)' : '#fecaca')}`,
    }} className="px-2.5 py-1 rounded-lg text-xs font-bold">
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  // Avatar colour based on first letter
  const avatarColors = [
    ['rgba(59,130,246,0.2)', '#60a5fa'], ['rgba(16,185,129,0.2)', '#34d399'],
    ['rgba(245,158,11,0.2)', '#fbbf24'], ['rgba(139,92,246,0.2)', '#a78bfa'],
    ['rgba(236,72,153,0.2)', '#f472b6'], ['rgba(239,68,68,0.2)', '#f87171'],
  ];
  const getAvatarStyle = (name: string) => {
    const idx = name.charCodeAt(0) % avatarColors.length;
    const [bg, text] = avatarColors[idx];
    return { backgroundColor: isDark ? bg : bg.replace('0.2', '0.12'), color: isDark ? text : text };
  };

  const renderUserRow = (user: any, depth = 0): React.ReactNode => {
    const isExpanded = expandedRows.has(user.id);
    const hasChildren = user.children && user.children.length > 0;
    const avatarStyle = getAvatarStyle(user.name);

    return (
      <React.Fragment key={user.id}>
        <tr
          className={`transition-colors ${!user.isActive ? 'opacity-50' : ''}`}
          style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}` }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          {/* Employee */}
          <td className="py-4 pr-6" style={{ paddingLeft: `${depth * 2.5 + 1.5}rem` }}>
            <div className="flex items-center gap-3">
              {hasChildren && !isFiltering ? (
                <button
                  type="button"
                  onClick={() => toggleExpand(user.id)}
                  className="w-6 h-6 flex items-center justify-center rounded-md transition-colors shrink-0"
                  style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff',
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    color: isDark ? '#94a3b8' : '#64748b',
                    boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                >
                  {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              ) : (
                <div className="w-6 h-6 shrink-0" />
              )}

              <div
                style={{
                  ...avatarStyle,
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  minHeight: '36px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                }}
              >
                {user.photoUrl ? (
                  <img src={user.photoUrl} alt={user.name} style={{ width: '36px', height: '36px', objectFit: 'cover', display: 'block' }} />
                ) : (
                  user.name.charAt(0).toUpperCase()
                )}
              </div>

              <div>
                <p className="font-semibold text-slate-800">{user.name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                      color: isDark ? '#94a3b8' : '#475569',
                    }}
                  >
                    {user.employeeId}
                  </span>
                  <span className="text-xs flex items-center gap-1" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                    <Mail className="w-3 h-3" /> {user.email}
                  </span>
                </div>
              </div>
            </div>
          </td>

          {/* Department & Role */}
          <td className="px-6 py-4">
            <p className="text-sm font-medium text-slate-700">{user.department || 'General'}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {getRoleBadge(user.role)}
              <span className="text-xs" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>{user.designation}</span>
            </div>
          </td>

          {/* Gender */}
          <td className="px-6 py-4">{getGenderBadge(user.gender)}</td>

          {/* Reports To */}
          <td className="px-6 py-4">
            <span className="text-sm font-medium" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
              {user.manager?.name || <span style={{ color: isDark ? '#475569' : '#94a3b8' }}>—</span>}
            </span>
          </td>

          {/* Status */}
          <td className="px-6 py-4">{getStatusBadge(user.isActive)}</td>

          {/* Actions */}
          <td className="px-6 py-4 text-right">
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" title="Edit User" onClick={() => openEditModal(user)}>
                <Edit className="w-4 h-4" style={{ color: isDark ? '#64748b' : '#94a3b8' }} />
              </Button>
              <Button variant="ghost" size="sm" title="Reset Password"
                onClick={() => { if (window.confirm('Reset password to NeuralNet@2026?')) resetPasswordMutation.mutate(user.id); }}>
                <KeyRound className="w-4 h-4" style={{ color: isDark ? '#60a5fa' : '#3b82f6' }} />
              </Button>
              {user.isActive && (
                <Button variant="ghost" size="sm" title="Deactivate" onClick={() => deactivateMutation.mutate(user.id)}>
                  <ShieldAlert className="w-4 h-4" style={{ color: isDark ? '#f87171' : '#ef4444' }} />
                </Button>
              )}
            </div>
          </td>
        </tr>

        {isExpanded && !isFiltering && hasChildren && user.children.map((child: any) => (
          renderUserRow(child, depth + 1)
        ))}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-end gap-2">
          <Button variant="secondary" className="shadow-sm">
            <UploadCloud className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button onClick={openCreateModal} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>

      {/* Main Table Card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--card-bg, #ffffff)',
          border: `1px solid var(--card-border, #e2e8f0)`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {/* Filters bar */}
        <div
          className="p-4 flex flex-col sm:flex-row gap-4 border-b"
          style={{
            backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : '#f8fafc',
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
          }}
        >
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-2.5" style={{ color: isDark ? '#64748b' : '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search by name, email or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none transition-colors"
              style={{
                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                color: isDark ? '#e2e8f0' : '#1e293b',
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: isDark ? '#64748b' : '#94a3b8' }} />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="py-2 px-3 rounded-lg text-sm outline-none transition-colors"
              style={{
                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                color: isDark ? '#e2e8f0' : '#1e293b',
              }}
            >
              <option value="">All</option>
              <option value="EMPLOYEE">User</option>
              <option value="MD">Super User</option>
              <option value="ORG_ADMIN">Org Admin</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
          <table className="w-full text-left text-sm">
            <thead
              className="text-xs uppercase font-semibold sticky top-0 z-10"
              style={{
                backgroundColor: isDark ? '#131f31' : '#f8fafc',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
              }}
            >
              <tr>
                {['Employee', 'Department & Role', 'Gender', 'Reports To', 'Status', 'Actions'].map((col, i) => (
                  <th
                    key={col}
                    className={`px-6 py-4 ${i === 5 ? 'text-right' : 'text-left'}`}
                    style={{ color: isDark ? '#64748b' : '#94a3b8', letterSpacing: '0.05em' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="p-4">
                    <div
                      className="h-20 animate-pulse rounded-lg"
                      style={{ backgroundColor: isDark ? '#1e293b' : '#f1f5f9' }}
                    />
                  </td>
                </tr>
              ) : isFiltering ? (
                filteredFlatUsers.map((user: any) => renderUserRow(user, 0))
              ) : (
                usersTree.map((user: any) => renderUserRow(user, 0))
              )}
            </tbody>
          </table>

          {((isFiltering && filteredFlatUsers.length === 0) || (!isFiltering && usersTree.length === 0)) && !isLoading && (
            <div className="p-8 text-center" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
              No users found matching your criteria.
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
          <div
            className="w-full max-w-2xl rounded-2xl shadow-2xl relative flex flex-col max-h-[90vh] animate-fade-in"
            style={{
              backgroundColor: 'var(--card-bg, #ffffff)',
              border: `1px solid var(--card-border, #e2e8f0)`,
            }}
          >
            {/* Modal header */}
            <div
              className="flex items-center justify-between px-6 py-5 border-b"
              style={{ borderColor: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9' }}
            >
              <h2 className="text-xl font-bold text-slate-800">{editingUser ? 'Edit User' : 'Add New User'}</h2>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <form id="userForm" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Full Name" name="name" defaultValue={editingUser?.name} required />
                  <Input label="Email Address" name="email" type="email" defaultValue={editingUser?.email} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Employee ID" name="employeeId" defaultValue={editingUser?.employeeId} required />
                  <Input label="Date of Joining" name="dateOfJoining" type="date" defaultValue={editingUser?.dateOfJoining?.split('T')[0]} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Department" name="department" defaultValue={editingUser?.department} required />
                  <Input label="Designation" name="designation" defaultValue={editingUser?.designation} required />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <Select label="Gender" name="gender" defaultValue={editingUser?.gender || ''}>
                    <option value="">-- Not Specified --</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </Select>
                </div>
                <div
                  className="grid grid-cols-2 gap-4 p-4 rounded-xl border"
                  style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                    borderColor: isDark ? '#334155' : '#e2e8f0',
                  }}
                >
                  <Select label="System Role" name="role"
                    defaultValue={editingUser?.role === 'MD' ? 'MD' : editingUser?.role === 'ORG_ADMIN' ? 'ORG_ADMIN' : 'EMPLOYEE'} required>
                    <option value="EMPLOYEE">User</option>
                    <option value="MD">Super User</option>
                    <option value="ORG_ADMIN">Org Admin</option>
                  </Select>
                  <Select label="Reports To" name="managerId" defaultValue={editingUser?.managerId || ''}>
                    <option value="">-- No Manager --</option>
                    {rawUsers.filter((u: any) => u.isActive).map((u: any) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.designation})</option>
                    ))}
                  </Select>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={onProbation}
                      onChange={e => setOnProbation(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-slate-700">On Probation</span>
                  </label>
                  {onProbation && (
                    <Input label="Probation End Date" name="probationEndDate" type="date"
                      defaultValue={editingUser?.probationEndDate?.split('T')[0]} />
                  )}
                  {!onProbation && <input type="hidden" name="probationEndDate" value="" />}
                </div>

                {!editingUser && (
                  <p
                    className="text-xs p-3 rounded-lg border"
                    style={{
                      backgroundColor: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff',
                      borderColor: isDark ? 'rgba(59,130,246,0.25)' : '#bfdbfe',
                      color: isDark ? '#93c5fd' : '#1e40af',
                    }}
                  >
                    <strong>Note:</strong> User's default password will be{' '}
                    <code
                      className="px-1 py-0.5 rounded text-xs"
                      style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#ffffff' }}
                    >
                      NeuralNet@2026
                    </code>. They will be required to change it on first login.
                  </p>
                )}
              </form>
            </div>

            {/* Modal footer */}
            <div
              className="px-6 py-4 flex justify-end gap-3 border-t rounded-b-2xl"
              style={{
                borderColor: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
              }}
            >
              <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" form="userForm" isLoading={createMutation.isPending || updateMutation.isPending}>
                {editingUser ? 'Save Changes' : 'Create User'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
